import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';

export function FotoLocal({ arquivo, ...props }) {
    const [url, setUrl] = useState('');
    useEffect(() => {
        if (!arquivo) return undefined;
        const value = URL.createObjectURL(arquivo);
        setUrl(value);
        return () => URL.revokeObjectURL(value);
    }, [arquivo]);
    return url ? <Box component="img" src={url} alt={arquivo.name || 'Foto selecionada'} {...props} /> : null;
}

export default function CapturarFoto({ onCapture, disabled = false, facingMode = 'environment',
    title = 'Fotografar o medidor mostrando a leitura e o lacre', nome = 'foto' }) {
    const mobile = useMediaQuery('(max-width: 900px)');
    const input = useRef(null), video = useRef(null), generation = useRef(0);
    const [open, setOpen] = useState(false), [foto, setFoto] = useState(null);
    const [erro, setErro] = useState(''), [ready, setReady] = useState(false), [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!open || foto || mobile) return undefined;
        let active = true, stream;
        setReady(false); setErro('');
        (async () => {
            try {
                if (!navigator.mediaDevices?.getUserMedia) throw new Error('A webcam requer navegador compatível e acesso seguro (HTTPS ou localhost). Use também a seleção de arquivos.');
                stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: {
                    facingMode: { ideal: facingMode }, width: { ideal: 1920 }, height: { ideal: 1080 },
                } });
                if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
                video.current.srcObject = stream;
                await video.current.play();
                if (active) setReady(true);
            } catch (e) {
                if (stream) stream.getTracks().forEach(t => t.stop());
                if (active) setErro(e.name === 'NotAllowedError' ? 'Permissão para usar a câmera não concedida. Você pode selecionar uma foto pelo botão de arquivos.'
                    : e.name === 'NotFoundError' ? 'Nenhuma câmera encontrada. Selecione uma foto pelo botão de arquivos.'
                    : e.name === 'NotReadableError' ? 'Câmera indisponível ou em uso por outro aplicativo.' : e.message || 'Não foi possível abrir a câmera.');
            }
        })();
        return () => {
            active = false;
            if (stream) stream.getTracks().forEach(t => t.stop());
        };
    }, [open, foto, mobile, facingMode]);
    useEffect(() => () => { generation.current += 1; }, []);
    function fechar() {
        generation.current += 1;
        setOpen(false); setFoto(null); setBusy(false); setReady(false); setErro('');
    }
    useEffect(() => { if (disabled) fechar(); }, [disabled]);
    function abrir() {
        if (disabled) return;
        setErro(''); setFoto(null); setReady(false);
        if (mobile) { input.current.value = ''; input.current.click(); }
        else setOpen(true);
    }
    async function capturar() {
        const v = video.current, token = generation.current;
        if (!v?.videoWidth || !v?.videoHeight || busy) return;
        setBusy(true);
        try {
            const canvas = document.createElement('canvas');
            canvas.width = v.videoWidth; canvas.height = v.videoHeight;
            canvas.getContext('2d').drawImage(v, 0, 0);
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95));
            if (token !== generation.current) return;
            if (!blob) throw new Error('Não foi possível capturar a foto.');
            setFoto(new File([blob], nome + '-' + Date.now() + '.jpg', { type: 'image/jpeg' }));
        } catch (e) { if (token === generation.current) setErro(e.message); }
        finally { if (token === generation.current) setBusy(false); }
    }
    return <>
        <Button type="button" startIcon={<PhotoCamera />} title={title} disabled={disabled} onClick={abrir}>Tirar foto</Button>
        <input hidden ref={input} type="file" accept="image/*" capture={facingMode} disabled={disabled}
            onChange={e => { const file = e.target.files?.[0]; if (file) { setFoto(file); setOpen(true); } e.target.value = ''; }} />
        <Dialog open={open} onClose={fechar} fullWidth maxWidth="md">
            <DialogTitle>{foto ? 'Conferir foto' : 'Capturar foto'}</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 1 }}>{title}</Alert>
                {erro && <Alert severity="error">{erro}</Alert>}
                {foto ? <FotoLocal arquivo={foto} sx={{ width: '100%', maxHeight: '65vh', objectFit: 'contain' }} />
                    : <video ref={video} autoPlay muted playsInline style={{ width: '100%', maxHeight: '65vh', background: '#000' }} />}
            </DialogContent>
            <DialogActions>
                <Button type="button" title="Descartar esta captura" onClick={fechar}>Cancelar</Button>
                {foto ? <>
                    <Button type="button" title="Descartar a prévia e fotografar novamente" onClick={() => { fechar(); abrir(); }}>Tirar outra</Button>
                    <Button type="button" title="Adicionar esta foto aos arquivos selecionados" disabled={disabled}
                        onClick={() => { onCapture(foto); fechar(); }}>Usar foto</Button>
                </> : <Button type="button" disabled={!ready || busy || disabled} onClick={capturar}>Capturar</Button>}
            </DialogActions>
        </Dialog>
    </>;
}
